import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CSSProperties } from 'react';
import config from 'config.json';

const MpPartialForm: React.FC = () => {
  const navigate = useNavigate();
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false); // Control de múltiples envíos
  const [tokenData, setTokenData] = useState<{ token: string; formData: any } | null>(null); // Estado para guardar el token y los datos
  const [amount, setAmount] = useState<string | null>(null);
  const [idPreferencia, setIdPreferencia] = useState<string | null>(null);
  const [tituloPreferencia, setTituloPreferencia] = useState<string>('Reserva de Hotel');

  useEffect(() => {
    const amount = sessionStorage.getItem('complete_payment_amount');
    const idPreferencia = sessionStorage.getItem('complete_payment_id');
    const userToken = sessionStorage.getItem('user_token');
    const tituloPreferencia = sessionStorage.getItem('titulo_prereserva') || 'Reserva de Hotel';
    const previousBook = sessionStorage.getItem('booking_id');

    setTituloPreferencia(tituloPreferencia);

    if (!amount || !idPreferencia || !userToken) {
      setErrorMessage('Falta información en el sistema para procesar el pago.');
      return;
    }

    setAmount(amount);
    setIdPreferencia(idPreferencia);

    const script = document.createElement('script');
    script.src = 'https://sdk.mercadopago.com/js/v2';
    script.type = 'text/javascript';
    script.async = true;
    script.onload = () => {
      const publicKey = config.mp_key;
      const mp = new (window as any).MercadoPago(publicKey, { locale: 'es-AR' });

      const scriptSecurity = document.createElement('script');
      scriptSecurity.src = 'https://www.mercadopago.com/v2/security.js';
      scriptSecurity.type = 'text/javascript';
      scriptSecurity.async = true;
      document.body.appendChild(scriptSecurity);

      const cardForm = mp.cardForm({
        amount: amount,
        iframe: true,
        form: {
          id: "form-checkout",
          cardNumber: { id: "form-checkout__cardNumber", placeholder: "Numero de tarjeta" },
          expirationDate: { id: "form-checkout__expirationDate", placeholder: "MM/YY" },
          securityCode: { id: "form-checkout__securityCode", placeholder: "Código de seguridad" },
          cardholderName: { id: "form-checkout__cardholderName", placeholder: "Titular de la tarjeta" },
          issuer: { id: "form-checkout__issuer", placeholder: "Banco emisor" },
          installments: { id: "form-checkout__installments", placeholder: "Cuotas" },
          identificationType: { id: "form-checkout__identificationType", placeholder: "Tipo de documento" },
          identificationNumber: { id: "form-checkout__identificationNumber", placeholder: "Número del documento" },
          cardholderEmail: { id: "form-checkout__cardholderEmail", placeholder: "E-mail" },
        },
        callbacks: {
          onFormMounted: (error: any) => {
            if (error) return console.error("Error al montar el formulario: ", error);
            console.log("Formulario montado correctamente.");
          },
          onSubmit: async (event: any) => {
            event.preventDefault();
            if (isSubmitting) return; // Prevenir múltiples envíos
            setIsSubmitting(true); // Deshabilitar el botón inmediatamente

            const formData = cardForm.getCardFormData();
            if (!formData.token) {
              setValidationMessage('Error al generar el token. Por favor, revisa los datos ingresados.');
              setIsSubmitting(false); // Habilitar nuevamente el botón si ocurre un error
              return;
            }

            // Guardar el token y los datos del formulario en el estado
            setTokenData({ token: formData.token, formData });
          },
          onError: (error: any) => {
            console.error('Error en el proceso de pago: ', error);
  
            setIsSubmitting(false); // Habilitar nuevamente el botón si ocurre un error
          }
        }
      });

      (window as any).cardForm = cardForm;
    };

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [amount, idPreferencia, tituloPreferencia]);

  // Cuando el token y los datos del formulario se actualicen, se hará el fetch
  useEffect(() => {
    if (tokenData) {
      handlePaymentSubmission(tokenData.token, tokenData.formData);
    }
  }, [tokenData]);

  const handlePaymentSubmission = async (token: string, formData: any) => {
    const cardholderName = (document.getElementById('form-checkout__cardholderName') as HTMLInputElement).value;
    const cardholderEmail = (document.getElementById('form-checkout__cardholderEmail') as HTMLInputElement).value;
    const identificationNumber = (document.getElementById('form-checkout__identificationNumber') as HTMLInputElement).value;

    if (!cardholderName || !cardholderEmail || !identificationNumber) {
      setValidationMessage('Por favor, complete todos los campos requeridos.');
      setIsSubmitting(false);
      return;
    }

    const requestBackend = {
      token,
      issuer_id: formData.issuerId,
      payment_method_id: formData.paymentMethodId,
      transaction_amount: parseFloat(amount || '0'),
      installments: Number(formData.installments),
      description: 'DNI: ' + formData.identificationNumber + '. ' + tituloPreferencia,
      payer: {
        first_name: cardholderName.split(' ').slice(0, -1).join(' '),
        last_name: cardholderName.split(' ').slice(-1).join(' '),
        email: formData.cardholderEmail,
        identification: {
          type: formData.identificationType,
          number: formData.identificationNumber,
        },
      },
      additional_info: {
        items: [
          {
            id: idPreferencia,
            title: tituloPreferencia,
            description: tituloPreferencia,
            currency_id: "ARS",
            unit_price: parseFloat(amount || '0'),
            quantity: 1,
            category_id: "travel"
          }
        ]
      },
      notification_url: `${config.backend_url}/notification`,
      external_reference: idPreferencia,
      preference_id: idPreferencia,
      statement_descriptor: 'Reserva - Setia Hotel',
      device_id: (window as any).MP_DEVICE_SESSION_ID,
      my_preference: idPreferencia,
      my_previous_book: sessionStorage.getItem('booking_id')
    };

    try {
      const response = await fetch(`${config.backend_url}/process_payment`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'SetiaAuthorization': `${sessionStorage.getItem('user_token')}`,
        },
        body: JSON.stringify(requestBackend),
      });

      const result = await response.json();

      if (result.estado === 'CONFIRMADA') {
        sessionStorage.setItem('operation_id', result.id_operacion);
        setPaymentStatus('success');
      } else {
        setPaymentStatus('failure');
      }
    } catch (error) {
      setPaymentStatus('failure');
      console.error("Error al procesar el pago: ", error);
    } finally {
      setIsSubmitting(false); // Volver a habilitar el botón tras la respuesta
    }
  };

  return (
    <div style={containerStyles}>
      {errorMessage && (
        <div style={errorStyles}>{errorMessage}</div>
      )}

      {paymentStatus === 'success' && (
        <div style={successStyles}>
          <p>¡Pago realizado con éxito!</p>
          <button onClick={() => navigate('/account-savelists')} style={buttonStyles}>Continuar</button>
        </div>
      )}

      {paymentStatus === 'failure' && (
        <div style={errorStyles}>
          <p>Error al procesar el pago. Por favor, inténtalo de nuevo más tarde.</p>
          <button onClick={() => navigate('/account-savelists')} style={buttonStyles}>Volver</button>
        </div>
      )}

      {!paymentStatus && (
        <>
          <h2 style={titleStyles}>Pagás con Mercadopago: {Number(amount).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</h2>

          {validationMessage && <div style={errorStyles}>{validationMessage}</div>}

          <form id="form-checkout" style={formStyles}>
            <div id="form-checkout__cardNumber" style={inputStyles}></div>
            <div id="form-checkout__expirationDate" style={inputStyles}></div>
            <div id="form-checkout__securityCode" style={inputStyles}></div>
            <input type="text" id="form-checkout__cardholderName" placeholder="Nombre como aparece en la tarjeta" style={inputStyles} />
            <select id="form-checkout__issuer" style={inputStyles}></select>
            <select id="form-checkout__installments" style={inputStyles}></select>
            <select id="form-checkout__identificationType" style={inputStyles}></select>
            <input type="text" id="form-checkout__identificationNumber" placeholder="Número del documento del titular" style={inputStyles} />
            <input type="email" id="form-checkout__cardholderEmail" placeholder="E-mail" style={inputStyles} />

            <button type="submit" id="form-checkout__submit" style={{ ...buttonStyles, cursor: isSubmitting ? 'not-allowed' : 'pointer', opacity: isSubmitting ? 0.6 : 1 }} disabled={isSubmitting}>
              {isSubmitting ? 'Procesando...' : 'Pagar'}
            </button>
          </form>

          <button onClick={() => navigate('/account-savelists')} style={buttonStyles}>
            Volver
          </button>
        </>
      )}
    </div>
  );
};

export default MpPartialForm;


// Estilos CSS como objetos para aplicar en línea
const containerStyles: CSSProperties = {
  backgroundColor: '#fff',
  padding: '20px',
  borderRadius: '8px',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  maxWidth: '600px',
  margin: '20px auto',
};

const formStyles: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
};

const titleStyles: CSSProperties = {
  textAlign: 'center',
  marginBottom: '20px',
  fontSize: '24px',
  fontWeight: '500',
};

const inputStyles: CSSProperties = {
  padding: '6px',
  fontSize: '14px',
  marginBottom: '15px',
  borderRadius: '50px',
  border: '1px solid #ccc',
  width: '100%',
  height: '38px',
};

const buttonStyles: CSSProperties = {
  backgroundColor: '#2563eb',
  color: 'white',
  fontSize: '18px',
  border: 'none',
  borderRadius: '50px',
  padding: '10px',
  cursor: 'pointer',
  width: '100%',
  marginTop: '10px',
};

const errorStyles: CSSProperties = {
  color: 'red',
  textAlign: 'center',
  marginBottom: '15px',
};

const successStyles: CSSProperties = {
  color: 'green',
  textAlign: 'center',
  marginBottom: '15px',
};
